import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'

import { instructorAtom, selectedInstructorIdAtom, selectedSkiResortAtom } from '../../contexts/AtomPrivateSessionSearch'
import { LOCATIONS } from '../../share/Constant'

/**
 * 用于处理教练选择功能的自定义Hook
 * 管理地点、运动类型、教练的选择，以及它们之间的关联关系
 *
 * @param {Array} availableSessions - 可用的课程会话数据
 * @param {Array} instructors - 教练列表
 * @returns {Object} - 包含状态和处理函数的对象
 */
export const useInstructorSelection = (availableSessions = [], instructors = []) => {
  // 状态
  const [location, setLocation] = useState('')
  const [sportType, setSportType] = useState('')
  const [selectedInstructorId, setSelectedInstructorId] = useState('')
  const [instructorDetail, setInstructorDetail] = useState()
  const [availableInstructors, setAvailableInstructors] = useState(new Set()) // Id of available instructors
  const [availableInstructorsDetail, setAvailableInstructorsDetail] = useState([])
  const [availableLocations, setAvailableLocations] = useState(LOCATIONS)
  const setInstructor = useSetAtom(instructorAtom)
  const setTsSelectedInstructorId = useSetAtom(selectedInstructorIdAtom)
  const setTsSelectedSkiResort = useSetAtom(selectedSkiResortAtom)

  // 处理地点选择
  const handleLocationChange = (locValue) => {
    setLocation((prev) => (prev === locValue ? '' : locValue))
    // setInstructor('') // 重置教练选择
  }

  // 处理运动类型选择
  const handleSportTypeChange = (typeValue) => {
    setSportType((prev) => (prev === typeValue ? '' : typeValue))
    // setInstructor('') // 重置教练选择
  }

  // 处理教练选择
  const handleInstructorChange = useCallback((instValue) => {
    setSelectedInstructorId((prev) => (prev === instValue ? '' : instValue))
  }, [])

  // 根据可用会话和教练列表初始化可用地点
  useEffect(() => {
    if (availableSessions?.length) {
      setAvailableLocations(getLocations(availableSessions, instructors))
    }
  }, [availableSessions, instructors])

  useEffect(() => {
    if (selectedInstructorId === '') {
      setInstructorDetail(null)
      setInstructor(null)
      setTsSelectedInstructorId(null)
    } else {
      let instructor = instructors.find((inst) => inst.resourceId === selectedInstructorId)
      setInstructorDetail(instructor)
      setInstructor(instructor)
      setTsSelectedInstructorId(selectedInstructorId)
      // setInstructor(instructors.find((inst) => inst.resourceId === selectedInstructorId))
    }
  }, [selectedInstructorId, instructors, setInstructor])

  useEffect(() => {
    setAvailableInstructorsDetail(instructors.filter((inst) => availableInstructors.has(inst.resourceId)))
  }, [availableInstructors, instructors])

  // Auto Select
  useEffect(() => {
    if (availableInstructors.size === 1) {
      const nextInstructorId = availableInstructors.values().next().value

      setSelectedInstructorId(nextInstructorId)
      setTsSelectedInstructorId(nextInstructorId)

      // setSelectedInstructorId((prev) => (prev === nextInstructorId ? '' : nextInstructorId))
    } else if (availableInstructors.size > 1 || availableInstructors.size === 0) {
      setSelectedInstructorId('')
      setTsSelectedInstructorId('')
    }
  }, [availableInstructors])

  // 根据地点和运动类型过滤可用教练
  useEffect(() => {
    const availableIds = new Set(
      availableSessions.filter((session) => (!sportType || session.sport === sportType) && (!location || session.skiResort === location)).map((session) => session.instructorId),
    )
    setAvailableInstructors(availableIds)
    setTsSelectedSkiResort(location)
  }, [availableSessions, sportType, location])

  // 获取可用地点
  const getLocations = (sessions, instructors) => {
    if (!sessions?.length) return []

    const instructorIds = instructors.map((inst) => inst.resourceId)
    const filteredSessions = sessions.filter((session) => instructorIds.includes(session.instructorId))

    const locationSet = new Set(filteredSessions.map((session) => session.skiResort))
    return Array.from(locationSet).map((value) => {
      return (
        LOCATIONS.find((loc) => loc.value === value) || {
          value,
          label: value,
          icon: null,
        }
      )
    })
  }

  return {
    // 状态
    location,
    sportType,
    selectedInstructorId,
    availableLocations,

    instructorDetail,
    availableInstructorsDetail,

    // 处理函数
    handleLocationChange,
    handleSportTypeChange,
    handleInstructorChange,
  }
}

export default useInstructorSelection
